import React, { useCallback, useState, type SyntheticEvent } from "react";
import { Alert, Button, FieldError, InputGroup, Spinner, TextField } from "@heroui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { Envelope } from "@gravity-ui/icons";
import { type AuthError, sendPasswordResetEmail } from "firebase/auth";

import { AppRoutes } from "@domain/routing/routeConstants";
import { AuthLayout } from "./AuthLayout";
import { auth } from "@domain/api/firebase/firebase";
import { getFirebaseErrorMessageByError } from "@domain/api/firebase/firebaseErrorMessages";
import { logger } from "@domain/infrastructure/logger";

const PASSWORD_RESET_TITLE = <FormattedMessage id="app.reset_password.caption" />;

export const PasswordResetView = () => {
    const { formatMessage } = useIntl();
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState<string | null>(null);
    const [error, setError] = useState<AuthError | null>(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const validate = useCallback(() => {
        if (!email) {
            setEmailError(formatMessage({ id: "app.validation.error.field_empty" }));
            return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setEmailError(formatMessage({ id: "app.validation.error.invalid_email" }));
            return false;
        }
        setEmailError(null);
        return true;
    }, [email, formatMessage]);

    const handleSubmit = useCallback(
        async (e: SyntheticEvent) => {
            e.preventDefault();
            if (!validate()) {
                return;
            }
            setLoading(true);
            try {
                await sendPasswordResetEmail(auth, email);
                setShowSuccess(true);
                setEmail("");
            } catch (err: any) {
                logger.error({ err });
                setError(err);
            }
            setLoading(false);
        },
        [validate, email],
    );

    const clearMessages = useCallback(() => {
        setError(null);
        setShowSuccess(false);
        setEmailError(null);
    }, []);

    const onEmailChange = useCallback(
        (v: string) => {
            setEmail(v);
            clearMessages();
        },
        [clearMessages],
    );

    return (
        <AuthLayout title={PASSWORD_RESET_TITLE}>
            {showSuccess && (
                <Alert status="success" className="mb-4">
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>{formatMessage({ id: "app.reset_password.confirmation" })}</Alert.Title>
                    </Alert.Content>
                </Alert>
            )}
            {error && (
                <Alert status="danger" className="mb-4">
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>{formatMessage({ id: getFirebaseErrorMessageByError(error) })}</Alert.Title>
                    </Alert.Content>
                </Alert>
            )}

            {!showSuccess && (
                <>
                    <FormattedMessage id="app.reset_password.description" tagName="div" />

                    <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-4">
                        <TextField
                            aria-label="E-Mail"
                            isInvalid={Boolean(emailError)}
                            value={email}
                            onChange={onEmailChange}
                        >
                            <InputGroup>
                                <InputGroup.Input
                                    type="email"
                                    placeholder={formatMessage({ id: "app.global.email" })}
                                    autoComplete="username"
                                />
                                <InputGroup.Suffix>
                                    <Envelope className="size-4" />
                                </InputGroup.Suffix>
                            </InputGroup>
                            <FieldError>{emailError}</FieldError>
                        </TextField>
                        <Button type="submit" isPending={loading} fullWidth size="lg">
                            {({ isPending }) => (
                                <>
                                    {isPending ? <Spinner color="current" size="sm" /> : null}
                                    <FormattedMessage id="app.button.send" />
                                </>
                            )}
                        </Button>
                    </form>
                </>
            )}

            <div className="flex justify-between">
                <div>
                    <Link to={AppRoutes.authLogin}>
                        <FormattedMessage id="app.header.login" />
                    </Link>
                </div>
                <div>
                    <Link to={AppRoutes.authRegister}>
                        <FormattedMessage id="app.registration.caption" />
                    </Link>
                </div>
            </div>
        </AuthLayout>
    );
};
